import React, { useEffect, useState } from 'react';

import {
    GetContractTermsInput,
    GetContractsByIdInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';

import { isNil } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { RateCardHeader } from 'src/components/RateCardHeader';
import { ContractBreadcrumbTitle } from 'src/constants';
import { EDITOR_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useTitle } from 'src/hooks/useTitle';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';
import { getConversionModel, getRateCardBreadcrumbItems } from 'src/util/RateCards';

import { LoadingSkeleton } from '../LoadingSkeleton';
import { ViewContractTerms } from './ViewContractTerms';
import { ViewCoreRates } from './ViewCoreRates';
import { mapPrimeAMFContractToRateCard } from './mapPrimeAMFContractToRateCard';

interface IViewPrimeAMFRateCardProps {
    rateCardId: number;
    serviceId: number;
    rateCardValuesAdd?: IPrimeAMFRateCardValues;
    isEmbedded?: boolean;
}

export const ViewPrimeAMFRateCard = ({
    serviceId,
    rateCardValuesAdd,
    rateCardId,
    ...props
}: IViewPrimeAMFRateCardProps) => {
    const history = useHistory();

    const { setTopAlertMsg, userRole } = React.useContext(AppContext);
    const isEmbedded = props.isEmbedded ?? false;

    const [currentTab, setCurrentTab] = useState('contract-terms');
    const [rateCardValues, setRateCardValues] = useState<IPrimeAMFRateCardValues>();
    const [dataLoaded, setDataLoaded] = React.useState<boolean>(false);
    const isReadOnly = isNil(rateCardValuesAdd);
    useTitle(`View rate card: ${rateCardValues?.contractName}`);

    useEffect(() => {
        if (isNil(rateCardValuesAdd)) {
            const contractPromise = contractConfigurationClient
                .getContractsById(
                    GetContractsByIdInput.clone({
                        contractId: rateCardId,
                        excludeRoyaltyModelTypes: true,
                        royaltyModelCodes: ['MAU', 'CPP'],
                        includeDraft: true,
                        includeIneligibleContractDeals: true
                    })
                )
                .toPromise();

            const contractTermsPromise = contractConfigurationClient
                .getContractTerms(GetContractTermsInput.clone({ contractId: rateCardId }))
                .toPromise();

            Promise.all([contractPromise, contractTermsPromise])
                .then((response) => {
                    const [contractResponse, contractTermsResponse] = response;
                    if (contractResponse.contracts.length > 0) {
                        const mappedRateCardValues = mapPrimeAMFContractToRateCard(
                            contractResponse,
                            contractTermsResponse
                        );
                        setRateCardValues(mappedRateCardValues);
                    }
                })
                .catch((error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('Contract APIs Unexpected error', error);
                    setTopAlertMsg(error.receivedException.message);
                })
                .finally(() => setDataLoaded(true));
        } else {
            setRateCardValues(rateCardValuesAdd);
            setDataLoaded(true);
        }
    }, []);

    return (
        <Column alignmentHorizontal="center" height="100%" spacingInset="200" data-testid="view-prime-amf-rate-card">
            {dataLoaded && isNil(rateCardValues) ? (
                // Rate card not found - show message without tabs
                <Column width="100%" height="100%" spacing="400">
                    {isReadOnly && !isEmbedded && (
                        <BreadcrumbWrapper
                            breadcrumbItems={getRateCardBreadcrumbItems(ContractBreadcrumbTitle.ViewContract)}
                        />
                    )}
                    <Text type="h300">Rate card not found</Text>
                </Column>
            ) : (
                // Loading or data available - show full layout with tabs
                <Column width="100%" height="100%" spacing="400" heights={['fit', 'fill']}>
                    {isReadOnly && (
                        <Column>
                            {!isEmbedded && (
                                <BreadcrumbWrapper
                                    breadcrumbItems={getRateCardBreadcrumbItems(ContractBreadcrumbTitle.ViewContract)}
                                />
                            )}
                            {!dataLoaded ? (
                                <Box type="fill" spacingInset="400">
                                    <Column spacing="200">
                                        <Skeleton count={5} />
                                    </Column>
                                </Box>
                            ) : (
                                <RateCardHeader rateCardHeaderValues={rateCardValues!}>
                                    {!isEmbedded && (
                                        <Button
                                            type="link"
                                            size="small"
                                            href={`${ROUTES.RATE_CARDS.INDEX}${
                                                rateCardValues?.isDraft ? ROUTES.RATE_CARDS.ADD : ROUTES.RATE_CARDS.EDIT
                                            }/${rateCardId}`}
                                            onClick={history.push}
                                            disabled={!EDITOR_ROLE_SET.has(userRole)}
                                        >
                                            <Icon className="button-icon" tokens={editTokens} />
                                            Edit contract
                                        </Button>
                                    )}
                                </RateCardHeader>
                            )}
                        </Column>
                    )}

                    <Row alignmentVertical="stretch" wrap="down" widths={['fill']} spacingInset="400 none none none">
                        <Column height="100%">
                            <TabGroup value={currentTab} onChange={setCurrentTab} fill="line">
                                <Tab value="contract-terms">Contract terms</Tab>
                                <Tab value="core-rates">Core rates</Tab>
                            </TabGroup>
                            <Column height="100%">
                                {currentTab === 'contract-terms' &&
                                    (dataLoaded && rateCardValues ? (
                                        <ViewContractTerms
                                            primeContractTerms={rateCardValues.primeContractTerms}
                                            amfContractTerms={rateCardValues.amfContractTerms}
                                            serviceId={serviceId}
                                        />
                                    ) : (
                                        <LoadingSkeleton />
                                    ))}
                                {currentTab === 'core-rates' && (
                                    <ViewCoreRates
                                        rateCardRowData={rateCardValues?.rateCardRowData ?? { cpp: [], mau: [] }}
                                        primeConversion={getConversionModel(
                                            rateCardValues?.primeContractTerms?.costPerPlay?.conversions
                                        )}
                                        primeTertiaryConversion={getConversionModel(
                                            rateCardValues?.primeContractTerms?.costPerPlay?.conversions,
                                            true
                                        )}
                                        amfConversion={rateCardValues?.amfContractTerms?.adRevShare?.conversions?.[0]}
                                        dataLoaded={dataLoaded}
                                    />
                                )}
                            </Column>
                        </Column>
                    </Row>
                </Column>
            )}
        </Column>
    );
};
